import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import './EditarEventos.css';
import './EditarEventos-upload.css';
import { ReactComponent as SubirArchivo } from '../assets/icon/subirarchivo.svg';
import { ReactComponent as Volver } from '../assets/icon/volver.svg';
import { useNavigate, useParams } from "react-router-dom";
import CalendarInicioEventos from './CalendarInicioEventos';
import CalendarFinEventos from './CalendarFinEventos';
import { imageService, eventService } from '../services';
import { useToast } from '../contexts/ToastContext';
import { 
  UPLOAD_STATUS, 
  SUCCESS_MESSAGES,
  validateEventData,
  formatDateForAPI,
  parseDate
} from '../utils';

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const MIN_WIDTH = 500;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 1080;
const MAX_HEIGHT = 1080;

const validateImageDimensions = (file) => {
    if (!file) return { isValid: false, errors: ['No se seleccionó ningún archivo'] };
    if (!file.type.startsWith("image/")) {
        return { isValid: false, errors: ['Tipo de archivo no permitido'] };
    }
    if (file.size > MAX_FILE_SIZE) {
        return { isValid: false, errors: ['El archivo no debe exceder 1MB'] };
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const width = img.width;
            const height = img.height;
            const errors = [];
            if (width < MIN_WIDTH || height < MIN_HEIGHT) {
                errors.push(`La imagen debe ser mínimo ${MIN_WIDTH}x${MIN_HEIGHT}px`);
            }
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                errors.push(`La imagen debe ser máximo ${MAX_WIDTH}x${MAX_HEIGHT}px`);
            }
            resolve({ isValid: errors.length === 0, errors });
        };
        img.onerror = () => {
            resolve({ isValid: false, errors: ['Error al procesar la imagen'] });
        };
        img.src = URL.createObjectURL(file);
    });
};

// Hoist static JSX elements following Vercel best practices
const UploadPlaceholderContent = ({ onFileSelect }) => (
  <>
        <div className="iconSubirArchivo"><div className="svgSubirArchivo"><SubirArchivo className="logosvgSubirArchivo" /></div></div>
        <p>Seleccione o arrastre aquí los archivos o</p>
        <label className="link">
            haga click para seleccionar
            <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && onFileSelect) {
                        onFileSelect(file);
                    }
                }}
                hidden
            />
        </label>
       <p>(Peso m&aacute;ximo 1MB)</p>
    </>
);

const ImageInfoModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="info-modal" ref={modalRef}>
                <h3>Medidas de Imagen Soportadas</h3>
                <ul>
                    <li><strong>Ancho:</strong> {MIN_WIDTH} - {MAX_WIDTH} pixels</li>
                    <li><strong>Alto:</strong> {MIN_HEIGHT} - {MAX_HEIGHT} pixels</li>
                    <li><strong>Peso máximo:</strong> 1MB</li>
                    <li><strong>Formatos:</strong> JPEG, PNG</li>
                </ul>
                <button className="modal-close-btn" onClick={onClose}>Cerrar</button>
            </div>
        </div>
    );
};

function EditarEventos() {
    const navigate = useNavigate();
    const { idEvento } = useParams();
    const { showSuccess, showError } = useToast();
    const [loadingData, setLoadingData] = useState(true);

    const [preview, setPreview] = useState(null);
    const [nombre, setNombre] = useState("");
    const [ubicacion, setUbicacion] = useState("");
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageUrl, setImageUrl] = useState("");
    const [imageFileName, setImageFileName] = useState("");
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (idEvento) {
            loadEvento(idEvento);
        } else {
            setLoadingData(false);
        }
    }, [idEvento]);

    const loadEvento = async (eventoId) => {
        setLoadingData(true);
        try {
            const result = await eventService.getById(eventoId);
            if (result.success && result.error === 0 && result.data) {
                const evento = result.data.data || result.data;
                
                const convertToCalendarFormat = (dateString) => {
                    if (!dateString) return "";
                    let dateStr = dateString;
                    if (!dateString.includes('T') && !dateString.includes(' ')) {
                        dateStr = dateString + "T12:00:00";
                    }
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return "";
                    const day = String(date.getDate()).padStart(2, '0');
                    const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
                    const month = months[date.getMonth()];
                    const year = date.getFullYear();
                    return `${day} de ${month} del ${year}`;
                };
                
                setNombre(evento.Titulo || evento.titulo || "");
                setUbicacion(evento.Ubicacion || evento.ubicacion || "");
                setFechaInicio(convertToCalendarFormat(evento.Fecha_creacion || evento.fechaCreacion));
                setFechaFin(convertToCalendarFormat(evento.Fecha_fin || evento.fechaFin));
                if (evento.Url_File || evento.url_File) {
                    setPreview(evento.Url_File || evento.url_File);
                    setImageUrl(evento.Url_File || evento.url_File);
                    setImageFileName(evento.FileName || evento.fileName || "");
                }
            } else {
                alert(result.mensaje || 'Error al cargar los datos del evento');
                navigate("/dashboard/Eventos");
            }
        } catch (error) {
            console.error('Error al cargar evento:', error);
            setUploadError('Error al cargar los datos del evento');
        } finally {
            setLoadingData(false);
        }
    };

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        
        try {
            const validation = await validateImageDimensions(file);
            if (!validation.isValid) {
                setUploadError(validation.errors.join(', '));
                setUploadStatus(UPLOAD_STATUS.ERROR);
                return;
            }
            
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            setUploadStatus(UPLOAD_STATUS.IDLE);
            setUploadError("");
            
            // Subir imagen inmediatamente
            setUploadStatus(UPLOAD_STATUS.UPLOADING);
            const result = await imageService.upload(file, {
                onProgress: setUploadProgress,
                skipDuplicate: true
            });
            
            if (result.success) {
                setImageUrl(result.url);
                setImageFileName(result.fileName);
                setUploadStatus(UPLOAD_STATUS.SUCCESS);
            } else {
                throw new Error(result.message || 'Error al subir imagen');
            }
            
        } catch (error) {
            setUploadError(error.message);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    const removeImage = useCallback(async () => {
        if (imageFileName) {
            try {
                await imageService.deleteImage(imageFileName);
            } catch (error) {
                console.warn('No se pudo eliminar la imagen del servidor:', error.message);
            }
            
            try {
                const cachedHashes = JSON.parse(localStorage.getItem('uploadedImages') || '{}');
                const entriesToDelete = Object.entries(cachedHashes).filter(([key, value]) => value.includes(imageFileName));
                entriesToDelete.forEach(([key]) => {
                    delete cachedHashes[key];
                });
                localStorage.setItem('uploadedImages', JSON.stringify(cachedHashes));
            } catch (error) {
                console.warn('No se pudo limpiar el cache de localStorage:', error.message);
            }
        }
        setPreview(null);
        setSelectedFile(null);
        setImageUrl("");
        setImageFileName("");
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        setUploadProgress(0);
    }, [imageFileName]);

    const eventData = useMemo(() => ({
        titulo: nombre,
        ubicacion: ubicacion,
        fechaInicio: formatDateForAPI(fechaInicio, 'start'),
        fechaFin: formatDateForAPI(fechaFin, 'end'),
        imagenUrl: imageUrl || preview
    }), [nombre, ubicacion, fechaInicio, fechaFin, imageUrl, preview]);

    const validateForm = useCallback(() => {
        const validation = validateEventData(eventData);
        if (!validation.isValid) {
            setUploadError(validation.errors.join(', '));
            return false;
        }
        
        if (validation.warnings.length > 0) {
            console.warn('Warnings:', validation.warnings);
        }
        
        return true;
    }, [eventData]);

    const extractMonthFromDate = (dateString) => {
        if (!dateString) return null;
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) return null;
            return date.getMonth() + 1;
        } catch (error) {
            console.warn('Error extracting month from date:', error);
            return null;
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        setUploadError("");
        
        try {
            const finalImageUrl = imageUrl || preview;
            const mesCalculado = extractMonthFromDate(fechaFin);
            
            const eventData = {
                titulo: nombre,
                ubicacion: ubicacion,
                fechaCreacion: formatDateForAPI(fechaInicio, 'start'),
                fechaFin: formatDateForAPI(fechaFin, 'end'),
                mes: mesCalculado,
                url_File: finalImageUrl,
                fileName: imageFileName
            };
            
            let result;
            if (idEvento) {
                result = await eventService.update(idEvento, eventData);
                if (result.success) {
                    showSuccess('Evento actualizado correctamente');
                    navigate("/dashboard/Eventos");
                } else {
                    showError(result.mensaje || 'Error al actualizar el evento');
                    setIsSubmitting(false);
                    return;
                }
            } else {
                result = await eventService.create(eventData);
                if (result.success) {
                    showSuccess('Evento creado correctamente');
                    navigate("/dashboard/Eventos");
                } else {
                    showError(result.mensaje || 'Error al crear el evento');
                    setIsSubmitting(false);
                    return;
                }
            }
            
        } catch (error) {
            console.error('Error en handleSubmit:', error);
            setUploadError(error.message);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='bodyAgregarEventos'>

            {/* TÍTULO */}
            <div className='tituloAgregarEventos'><p>-Evento</p></div>
            {loadingData ? (
                <div className="loading-container">
                    <p>Cargando datos del evento...</p>
                </div>
            ) : (
                <>
            <div className='cuerpoBotonVolverEventos'>
                <button
                    className='botonesAgregarEventos'
                    onClick={() => navigate("/dashboard/Eventos")}
                >
                    <div className="svgBotones"><Volver className="logo-svgBotones" /></div>
                    Volver
                </button>
            </div>

            <div className='cuerpoAgregarEventos'>

                {/* SUBIR IMAGEN */}
                <div className="logoUpload">
                    <div className="label-with-info">
                        <p className="label">Imagen</p>
                        <button 
                            id="evento-info-btn"
                            className="info-button" 
                            onClick={() => setShowInfo(true)}
                            type="button"
                            title="Ver medidas soportadas"
                        >
                            ?
                        </button>
                    </div>
                    <div
                        className="uploadBox"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
{preview ? (
                            <>
                                <img src={preview} alt="preview" className="preview" />

                                {/* BOTONES CAMBIAR / ELIMINAR */}
                                <div className="actions">
                                    <label className="actionButton cambiar">
                                        Cambiar
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFile(e.target.files[0])}
                                            hidden
                                        />
                                    </label>

                                    <button className="actionButton eliminar" onClick={removeImage}>
                                        Eliminar
                                    </button>
                                </div>
                            </>
                        ) : <UploadPlaceholderContent onFileSelect={handleFile} />}
                    </div>
                </div>

                {/* DROPDOWN ESTADOS */}
                <div className='inputsEventos'>
                    {/* nombre */}
                    <div className="input-groupAgregarEventos">
                        <div className='text-inputAgregarEventos'>
                            <label htmlFor="TituloAgregarEventos">Titulo</label>
                        </div>
                        <input
                            className="inputAgregarEventos"
                            type="Text"
                            id="text"
                            maxLength={100}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="6B Invent Germany"
                        />
                        <div className="contador">{nombre.length}/100</div>
                    </div>
                    {/* Calendario de fecha de inicio */}
                    <div className='calendario'>
                        <CalendarInicioEventos onSelect={setFechaInicio} value={fechaInicio} />
                        <div className="contadorFantasma">0/30</div>
                    </div>

                    {/* Calendario de fecha de fin */}
                    <div className='calendario'>
                        <CalendarFinEventos onSelect={setFechaFin} value={fechaFin} />
                        <div className="contadorFantasma">0/30</div>
                    </div>

                    {/* ubicacion */}
                    <div className="input-groupAgregarEventos">
                        <div className='text-inputAgregarEventos'>
                            <label htmlFor="TituloAgregarEventos">Ubicación</label>
                        </div>
                        <textarea
                            className="AgregarEventostextareaUbicacion"
                            maxLength={250}
                            value={ubicacion}
                            onChange={(e) => setUbicacion(e.target.value)}
                            placeholder="6B Invent Germany"
                            rows={1}
                        />
                        <div className="contador">{ubicacion.length}/250</div>
                    </div>
                    {/* Estado de subida - Using explicit conditional rendering following Vercel best practices */}
                    {uploadStatus !== UPLOAD_STATUS.IDLE ? (
                        <div className="upload-status">
                            {uploadStatus === UPLOAD_STATUS.UPLOADING ? (
                                <div className="progress-container">
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="progress-text">Subiendo imagen... {uploadProgress}%</p>
                                </div>
                            ) : uploadStatus === UPLOAD_STATUS.SUCCESS ? (
                                <div className="success-message">
                                    <p>✓ Imagen subida exitosamente</p>
                                </div>
                            ) : uploadStatus === UPLOAD_STATUS.ERROR ? (
                                <div className="error-message">
                                    <p>✗ {uploadError}</p>
                                    <button 
                                        type="button" 
                                        className="retry-button"
                                        onClick={() => setUploadStatus(UPLOAD_STATUS.IDLE)}
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* botones */}
                    <div className='botonesAgregarEventos2'>
                        <button 
                            type="button" 
                            className="btn-acceptGuardarEventos"
                            onClick={handleSubmit}
                            disabled={isSubmitting || uploadStatus === UPLOAD_STATUS.UPLOADING}
                        >
                            {isSubmitting ? (idEvento ? 'Actualizando...' : 'Guardando...') : (idEvento ? 'Actualizar' : 'Guardar')}
                        </button>
                    </div>
                </div>

            </div>
                </>
            )}
            <ImageInfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
        </div >
    );
}
export default EditarEventos;
